<template>
    <Dialog v-model:visible="visible" :header="`Assign Template: ${template.name}`" :modal="true"
        :closeOnEscape="closeOnEscape" :style="{ width: '40rem' }" :breakpoints="{ '768px': '95vw' }">
        <!-- Conflict confirmation: nothing has been written yet -->
        <div v-if="step === ASSIGN_STEP.CONFIRM_REPLACE" class="space-y-3" data-testid="assign-conflict-step">
            <Message severity="warn" :closable="false">
                {{ conflicts.length === 1 ? 'One programme-semester already uses' : `${conflicts.length} programme-semesters already use` }}
                a different template. Nothing has been changed yet — replace
                {{ conflicts.length === 1 ? 'it' : 'them' }} with “{{ template.name }}”?
            </Message>
            <ul class="max-h-60 overflow-y-auto divide-y divide-surface-200 border border-surface-200 rounded-md"
                data-testid="assign-conflict-list">
                <li v-for="conflict in conflicts" :key="`${conflict.programme_id}:${conflict.academic_calendar_id}`"
                    class="px-3 py-2 text-sm flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span class="font-medium">{{ conflict.programme_name }}</span>
                    <span class="text-muted-color">·</span>
                    <span>{{ conflict.calendar_name }}</span>
                    <span class="text-muted-color">·</span>
                    <span class="text-muted-color">currently {{ conflict.current_template_name }}</span>
                </li>
            </ul>
        </div>

        <div v-else class="space-y-4">
            <!-- 1. Apply to -->
            <div class="space-y-1.5">
                <span id="assign-apply-mode-label" class="block text-sm font-medium">Apply to</span>
                <SelectButton v-model="mode" :options="applyModeOptions" optionLabel="label" optionValue="value"
                    optionDisabled="disabled" :allowEmpty="false" :disabled="saving"
                    aria-labelledby="assign-apply-mode-label" data-testid="assign-apply-mode" />
                <small v-if="hasHistoricalColumns" class="block text-muted-color" data-testid="assign-historical-note">
                    This template reads data from earlier semesters, so it can't be a default for all semesters.
                    Assign it to one semester at a time.
                </small>
                <small v-else-if="mode === APPLY_MODE.ALL" class="block text-muted-color">
                    Becomes each programme's default for every semester without its own assignment, including future ones.
                </small>
            </div>

            <!-- 2. Programmes -->
            <div class="space-y-1">
                <label for="assign-programmes" class="block text-sm font-medium">Programmes</label>
                <MultiSelect v-model="programmeIds" inputId="assign-programmes" @show="onOverlayShow" @hide="onOverlayHide" :options="programmeOptions"
                    optionLabel="program_name" optionValue="id" placeholder="Select one or more programmes" class="w-full"
                    :loading="loadingProgrammes" :disabled="saving" filter filterPlaceholder="Search programmes..."
                    display="chip" :maxSelectedLabels="3" data-testid="assign-prog-multiselect">
                    <template #option="{ option }">
                        <div class="flex flex-col py-0.5 min-w-0">
                            <div class="flex items-center gap-2">
                                <span class="font-medium">{{ option.program_name }}</span>
                                <span class="text-[10px] font-mono bg-surface-100 text-surface-600 border border-surface-200 px-1.5 py-0.5 rounded">
                                    {{ option.prog_code }}
                                </span>
                            </div>
                            <span class="text-xs text-muted-color">
                                {{ option.degree }} · {{ option.duration }} yrs ·
                                Default: <span data-testid="assign-prog-default">{{ option.defaultTemplateLabel }}</span>
                            </span>
                        </div>
                    </template>
                </MultiSelect>
                <small v-if="programmeIds.length === 0" class="text-muted-color">
                    Choose the programmes to assign this result template to — you can pick several at once
                </small>
            </div>

            <!-- 3. Semesters (specific mode only) -->
            <div v-if="mode === APPLY_MODE.SPECIFIC" class="space-y-1">
                <label for="assign-semesters" class="block text-sm font-medium">Semesters</label>
                <MultiSelect v-model="calendarIds" inputId="assign-semesters" @show="onOverlayShow" @hide="onOverlayHide" :options="calendarOptions"
                    optionLabel="label" optionValue="id" placeholder="Select one or more semesters" class="w-full"
                    :disabled="saving" filter filterPlaceholder="Search semesters..." display="chip"
                    :selectionLimit="semesterSelectionLimit" :showToggleAll="!hasHistoricalColumns"
                    data-testid="assign-semester-multiselect" />
                <small v-if="hasHistoricalColumns" class="text-muted-color">Only one semester can be chosen for this template.</small>
            </div>

            <!-- Selected programmes summary -->
            <div v-if="selectedProgrammes.length > 0" class="bg-primary/10 p-2.5 rounded-md border border-primary/20">
                <h4 class="font-semibold text-primary mb-2 text-sm">
                    Selected Programme{{ selectedProgrammes.length > 1 ? 's' : '' }} ({{ selectedProgrammes.length }})
                </h4>
                <div class="space-y-1 max-h-36 overflow-y-auto">
                    <div v-for="programme in selectedProgrammes" :key="programme.id"
                        class="flex flex-wrap items-center justify-between gap-x-2 bg-surface-0 border border-primary/20 rounded px-2 py-1 text-sm"
                        data-testid="assign-selected-programme">
                        <div class="flex items-center gap-2 min-w-0">
                            <span class="font-medium truncate">{{ programme.program_name }}</span>
                            <span class="text-[10px] font-mono bg-surface-100 text-surface-600 border border-surface-200 px-1.5 py-0.5 rounded shrink-0">
                                {{ programme.prog_code }}
                            </span>
                        </div>
                        <span class="text-xs text-muted-color shrink-0">
                            Default: {{ programme.defaultTemplateLabel }}
                        </span>
                    </div>
                </div>
            </div>

            <!-- Historical source mapping -->
            <div v-if="mode === APPLY_MODE.SPECIFIC && hasHistoricalColumns"
                class="bg-surface-50 p-2.5 rounded-md border border-surface-200" data-testid="assign-historical-mapping">
                <h3 class="font-semibold mb-2 flex items-center text-sm">
                    <i class="pi pi-history mr-2 text-primary"></i> Historical Data Mapping
                </h3>
                <p class="text-xs text-muted-color mb-3">
                    This template uses data from previous terms. Please specify where to find the data for each column.
                </p>

                <div class="space-y-3">
                    <div v-for="col in historicalColumns" :key="col.key" class="grid grid-cols-1 gap-1 items-start">
                        <label :for="`assign-source-${col.key}`" class="text-sm font-medium flex justify-between gap-2">
                            <span>{{ col.label }}</span>
                            <span v-if="col.calculation_arguments" class="text-xs text-muted-color">
                                Source: {{ col.calculation_arguments }}
                            </span>
                        </label>
                        <Select v-model="historicalMapping[col.key]" @show="onOverlayShow" @hide="onOverlayHide" :inputId="`assign-source-${col.key}`"
                            :options="calendarOptions" optionLabel="label" optionValue="id" :disabled="saving"
                            :placeholder="`Select Source ${labels.TERM}`" class="w-full p-inputtext-sm"
                            :data-testid="`assign-source-${col.key}`" />
                    </div>
                </div>
            </div>
        </div>

        <small v-if="step === ASSIGN_STEP.FORM && programmeIds.length > 0 && validationMessage"
            class="block mt-3 text-muted-color" data-testid="assign-validation-hint">
            {{ validationMessage }}
        </small>

        <Message v-if="errorMessage" severity="error" :closable="false" class="mt-3" data-testid="assign-error">
            {{ errorMessage }}
        </Message>

        <template #footer>
            <div v-if="step === ASSIGN_STEP.CONFIRM_REPLACE" class="flex flex-wrap justify-end gap-2 pt-4">
                <Button size="small" label="Back" icon="pi pi-arrow-left" text severity="secondary" :disabled="saving"
                    @click="backToForm" data-testid="btn-assign-conflict-back" />
                <Button size="small" :label="replaceLabel" icon="pi pi-check" severity="warn" :loading="saving"
                    @click="confirmReplace" data-testid="btn-assign-confirm-replace" />
            </div>
            <div v-else class="flex flex-wrap justify-end gap-2 pt-4">
                <Button size="small" label="Cancel" icon="pi pi-times" text @click="closeModal" severity="secondary"
                    :disabled="saving" data-testid="btn-assign-template-prog-cancel" />
                <Button size="small" :label="submitLabel" icon="pi pi-check" @click="submit" :loading="saving"
                    :disabled="!isValid" data-testid="btn-assign-template-prog-confirm" />
            </div>
        </template>
    </Dialog>
</template>

<script setup lang="ts">
import { useSchoolLabels } from '@/composables/useSchoolLabels';
import { useOverlayAwareEscape } from '@/composables/ui/useOverlayAwareEscape';
import {
  ASSIGN_STEP,
  useAssignTemplateToProgrammes,
  type AssignableTemplate,
} from '@/composables/results/useAssignTemplateToProgrammes';
import { APPLY_MODE } from '@/utils/programmeTemplateAssignment';

const props = defineProps<{
    template: AssignableTemplate;
}>();

const emit = defineEmits<{
    close: [];
    assigned: [];
}>();

const { labels } = useSchoolLabels();

const visible = ref(true);

watch(visible, (val) => {
  if (!val) emit('close');
});

function closeModal() {
  visible.value = false;
}

const {
  mode,
  programmeIds,
  calendarIds,
  historicalMapping,
  step,
  conflicts,
  errorMessage,
  saving,
  applyModeOptions,
  calendarOptions,
  hasHistoricalColumns,
  historicalColumns,
  isValid,
  loadingProgrammes,
  programmeOptions,
  replaceLabel,
  selectedProgrammes,
  semesterSelectionLimit,
  submitLabel,
  validationMessage,
  backToForm,
  confirmReplace,
  submit,
} = useAssignTemplateToProgrammes(() => props.template, {
  onSuccess: () => {
    emit('assigned');
    closeModal();
  },
});

const { closeOnEscape, onOverlayShow, onOverlayHide } = useOverlayAwareEscape();
</script>
